import { Modal } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../../components/Atoms';
import { useResetRegistryPasswordMutate } from '../hooks/useResetRegistryPasswordMutate';

interface ResetPasswordConfirmModalProps {
    opened: boolean;
    onClose: () => void;
    onSuccess: (newPassword: string) => void;
}

export default function ResetPasswordConfirmModal({
    opened,
    onClose,
    onSuccess,
}: ResetPasswordConfirmModalProps) {
    const { t } = useTranslation('artifactRegistry');

    const mutation = useResetRegistryPasswordMutate({
        onSuccess: (data) => {
            onClose();
            onSuccess(data.new_password);
        },
    });

    const handleConfirm = () => {
        mutation.mutate();
    };

    const handleClose = () => {
        if (mutation.isPending) return;
        onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            centered
            size="md"
            closeOnClickOutside={!mutation.isPending}
            closeOnEscape={!mutation.isPending}
            title={
                <div className="inline-flex items-center gap-2">
                    <IconAlertTriangle size={20} color="#eb5757" />
                    <span className="font-semibold">
                        {t('auth.confirm.title', 'Reset registry password?')}
                    </span>
                </div>
            }
        >
            <div className="w-full flex flex-col gap-4">
                <p className="text-sm text-fg/78">
                    {t(
                        'auth.confirm.body',
                        "This generates a new password and invalidates your current docker login. You'll need to log in again on every machine."
                    )}
                </p>
                <div className="w-full flex flex-row justify-end items-center gap-2">
                    <Button variant="secondary" onClick={handleClose} disabled={mutation.isPending}>
                        {t('auth.confirm.cancel', 'Cancel')}
                    </Button>
                    <Button variant="danger" onClick={handleConfirm} isLoading={mutation.isPending}>
                        {t('auth.confirm.action', 'Reset password')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
